import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DescriptionTable,
  DirtyFormAlert,
  Notice,
  SlidePane,
  SelectedFeaturesTags,
  SelectedCustomFieldsTags,
  IconInfo,
  NumberInput,
  Label,
  Tabs,
  TabsHeader,
  Tab,
  TabContent,
} from 'shared/components';
import { licenseTypes, errorMsg } from 'shared/constants';
import { validateRequiredNumber } from 'shared/validation';
import {
  displayValue,
  capitalizeFirstLetter,
  displayMaxOveragesValue,
  getSectionListData,
  mapProductsToSelector,
  getDefaultLicensePolicy,
  getRemainingLicenses,
  getPolicyHasMaxTransfers,
  preparePolicyFeatures,
  hasPermission,
  getCurrentUserCompany,
} from 'shared/helpers';
import { getUserLicenseTemplates, getUserProducts } from 'redux/user/actions';
import { normalizeProductListItem } from 'CreateOrderForm/helpers';
import {
  getInitialProductValue,
  getInitialValue,
  getPolicyValue,
} from './helpers';
import {
  ProductInformation,
  LicenseTypeSection,
  LicenseOptionsSection,
  LicenseUsageSection,
  ProductFeaturesTab,
  CustomFieldsTab,
} from './components';
import './styles.scss';

const getSelectedPolicyFeatures = (selectedPolicy) => {
  const features = get(selectedPolicy, 'license_product_feature_templates', []);
  return features;
};

const getPoliciesList = (policies, products, selectedProduct) => {
  const product = selectedProduct || get(products, '[0]');
  const list = policies.filter(
    (pol) => get(pol, 'product.id') === get(product, 'id'),
  );
  return list;
};

const SelectProductForm = ({
  closeCb,
  handleSubmit,
  isOpen,
  productToEdit,
  title,
  width,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const periodOptions = [
    { label: t('Years'), value: 'y' },
    { label: t('Months'), value: 'm' },
    { label: t('Days'), value: 'd' },
  ];
  const consumptionPeriodOptions = [
    { label: t('Daily'), value: 'daily' },
    { label: t('Weekly'), value: 'weekly' },
    { label: t('Monthly'), value: 'monthly' },
    { label: t('Annually'), value: 'annually' },
  ];

  const counter = useSelector((state) => get(state, 'user.counter', []));
  const userID = useSelector((state) => get(state, 'user.details.id'));
  const companyID = useSelector((state) =>
    get(getCurrentUserCompany(state), 'id'),
  );
  const productsSectionState = useSelector((state) =>
    userID ? get(state, `user.userSections.${userID}.products`, {}) : {},
  );
  const licenseTemplatesSectionState = useSelector((state) =>
    userID
      ? get(state, `user.userSections.${userID}.licenseTemplates`, {})
      : {},
  );
  const legacyProductsListState = useSelector((state) =>
    get(state, 'user.details.products', []),
  );
  const legacyUserPolicies = useSelector((state) =>
    get(state, 'user.details.license_templates', []),
  );

  useEffect(() => {
    if (!userID) {
      return;
    }

    dispatch(getUserProducts(userID));
    dispatch(getUserLicenseTemplates(userID));
  }, [dispatch, userID]);

  const productsForbidden = !!get(productsSectionState, 'forbidden');
  const licenseTemplatesForbidden = !!get(
    licenseTemplatesSectionState,
    'forbidden',
  );
  const isSectionForbidden = productsForbidden || licenseTemplatesForbidden;

  const productsSectionData = getSectionListData(
    get(productsSectionState, 'data'),
  );
  const hasProductsSectionData =
    Array.isArray(get(productsSectionState, 'data')) ||
    Array.isArray(get(get(productsSectionState, 'data'), 'results'));

  const productsListState = productsForbidden
    ? []
    : hasProductsSectionData
      ? productsSectionData
      : legacyProductsListState;

  const products = productsListState
    .map((productItem) => {
      const normalizedProduct = normalizeProductListItem(productItem);

      return {
        ...normalizedProduct.product,
        num_of_licenses: normalizedProduct.numOfLicense,
      };
    })
    .filter((product) => !get(product, 'is_bundle'));

  const licenseTemplatesSectionData = getSectionListData(
    get(licenseTemplatesSectionState, 'data'),
  );
  const hasLicenseTemplatesSectionData =
    Array.isArray(get(licenseTemplatesSectionState, 'data')) ||
    Array.isArray(get(get(licenseTemplatesSectionState, 'data'), 'results'));

  const userPolicies = licenseTemplatesForbidden
    ? []
    : hasLicenseTemplatesSectionData
      ? licenseTemplatesSectionData
      : legacyUserPolicies;

  const initialLicensePolicy = getDefaultLicensePolicy(
    products,
    userPolicies,
    productToEdit,
  );

  const [loading, setLoading] = useState(false);
  const [isFormDirty, setFormDirty] = useState(false);
  const [isDirtyFormDisplayed, setDirtyFormDisplay] = useState(false);
  const [useCustomValues, setUseCustomValues] = useState(false);
  const [customPolicyDirty, setCustomPolicyDirty] = useState(false);
  const [featuresDirty, setFeaturesDirty] = useState(false);
  const [customFieldsDirty, setCustomFieldsDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('licenseType');

  const resetCustomizationDirtyFlags = () => {
    setCustomPolicyDirty(false);
    setFeaturesDirty(false);
    setCustomFieldsDirty(false);
  };

  const markCustomPolicyDirty = () => {
    setFormDirty(true);
    setCustomPolicyDirty(true);
  };

  const markFeaturesDirty = () => {
    setFormDirty(true);
    setFeaturesDirty(true);
  };

  const markCustomFieldsDirty = () => {
    setFormDirty(true);
    setCustomFieldsDirty(true);
  };

  const handleUseCustomValuesToggle = (val) => {
    setUseCustomValues(val);
    if (!val) {
      resetCustomizationDirtyFlags();
    }
  };

  // ------------- PRODUCT INFORMATION ----------------------------------------------------------------------------------------------------------------------------------
  const [selectedProduct, setSelectedProduct] = useState({
    label: getInitialProductValue('product_name', products, productToEdit),
    value: getInitialProductValue('product_name', products, productToEdit),
    data: productToEdit || get(products, '[0]'),
  });
  const [policies, setPolicies] = useState(
    getPoliciesList(userPolicies, products),
  );
  const [selectedPolicy, setSelectedPolicy] = useState(initialLicensePolicy);
  const [selectedPolicyFeatures, setSelectedPolicyFeatures] = useState(
    preparePolicyFeatures(getSelectedPolicyFeatures(initialLicensePolicy)),
  );

  // ------------- LICENSE POLICY ---------------------------------------------------------------------------------------------------------------------------------------
  const [selectedPolicyCustomFields, setSelectedPolicyCustomFields] = useState(
    get(selectedPolicy, 'license_custom_field_templates'),
  );

  const selectedProductID = get(selectedProduct, 'data.id');

  useEffect(() => {
    const shouldHydrateProductState =
      !selectedProductID || !selectedPolicy || !policies.length;

    if (
      !shouldHydrateProductState ||
      !products.length ||
      !userPolicies.length
    ) {
      return;
    }

    const existingSelectedProduct = products.find(
      (product) => get(product, 'id') === selectedProductID,
    );
    const defaultProduct =
      existingSelectedProduct || productToEdit || get(products, '[0]');
    const defaultPolicy = getDefaultLicensePolicy(
      products,
      userPolicies,
      defaultProduct,
    );

    setSelectedProduct({
      label: get(defaultProduct, 'product_name'),
      value: get(defaultProduct, 'product_name'),
      data: defaultProduct,
    });
    setPolicies(getPoliciesList(userPolicies, products, defaultProduct));
    setSelectedPolicy(defaultPolicy);
    setSelectedPolicyFeatures(
      preparePolicyFeatures(getSelectedPolicyFeatures(defaultPolicy)),
    );
    setSelectedPolicyCustomFields(
      get(defaultPolicy, 'license_custom_field_templates'),
    );
  }, [
    selectedProductID,
    selectedPolicy,
    policies,
    products,
    userPolicies,
    productToEdit,
  ]);
  const [licenseQuantity, setLicenseQuantity] = useState(
    getInitialProductValue('license_num', products, productToEdit, 1),
  );
  const [licenseQuantityError, setLicenseQuantityError] = useState('');
  // -------------------------------------------------------------------------------------------------------------------------------------------------------------------
  const [preventMaxTransfers, setPreventMaxTransfers] = useState(
    get(initialLicensePolicy, 'max_transfers') === -1,
  );
  const [hasMaxTransfers, setHasMaxTransfers] = useState(
    getPolicyHasMaxTransfers(initialLicensePolicy),
  );
  const [maxTransfers, setMaxTransfers] = useState(
    getInitialValue('max_transfers', initialLicensePolicy, productToEdit, 0),
  );
  const [maxTransfersError, setMaxTransfersError] = useState('');

  // ------------- CUSTOM LICENSE POLICY STATE --------------------------------------------------------------------------------------------------------------------------
  const [customLicenseType, setCustomLicenseType] = useState(
    get(initialLicensePolicy, 'default_license_type'),
  );
  const [customSubscriptionPeriodValue, setCustomSubscriptionPeriodValue] =
    useState(1);
  const [customSubscriptionPeriodLabel, setCustomSubscriptionPeriodLabel] =
    useState('y');
  const [customAllowGracePeriod, setCustomAllowGracePeriod] = useState(false);
  const [customGracePeriod, setCustomGracePeriod] = useState(0);
  const [customSelectedValidityType, setCustomSelectedValidityType] =
    useState('valid_from');
  const [customTimeLimitedPeriodValue, setCustomTimeLimitedPeriodValue] =
    useState(1);
  const [customTimeLimitedPeriodLabel, setCustomTimeLimitedPeriodLabel] =
    useState('d');
  const [customTimeLimitedEndDate, setCustomTimeLimitedEndDate] = useState('');
  const [customIsTrial, setCustomIsTrial] = useState(false);
  const [customSelectedTrialType, setCustomSelectedTrialType] =
    useState('trial_days');
  const [customTrialDays, setCustomTrialDays] = useState(0);
  const [customTrialEndDate, setCustomTrialEndDate] = useState('');
  const [customMaxConsumptions, setCustomMaxConsumptions] = useState(
    get(initialLicensePolicy, 'max_consumptions'),
  );
  const [
    customAllowUnlimitedConsumptions,
    setCustomAllowUnlimitedConsumptions,
  ] = useState(get(initialLicensePolicy, 'allow_unlimited_consumptions'));
  const [customAllowNegativeConsumptions, setCustomAllowNegativeConsumptions] =
    useState(get(initialLicensePolicy, 'allow_negative_consumptions'));
  const [customAllowOverages, setCustomAllowOverages] = useState(
    get(initialLicensePolicy, 'allow_overages'),
  );
  const [customSelectedOverageType, setCustomSelectedOverageType] =
    useState('overages-unlimited');
  const [customMaxOverages, setCustomMaxOverages] = useState(
    get(initialLicensePolicy, 'max_overages'),
  );
  const [customResetConsumption, setCustomResetConsumption] = useState(
    get(initialLicensePolicy, 'reset_consumption'),
  );
  const [customResetConsumptionPeriod, setCustomResetConsumptionPeriod] =
    useState(get(initialLicensePolicy, 'consumption_period'));
  const [customMaxActivations, setCustomMaxActivations] = useState(
    get(initialLicensePolicy, 'max_activations'),
  );
  const [customAllowUnlimitedActivations, setCustomAllowUnlimitedActivations] =
    useState(get(initialLicensePolicy, 'allow_unlimited_activations'));
  const [customPreventVm, setCustomPreventVm] = useState(
    get(initialLicensePolicy, 'prevent_vm'),
  );
  const [customMaxLicenseUsers, setCustomMaxLicenseUsers] = useState(
    get(initialLicensePolicy, 'max_license_users'),
  );
  const [customUnlimitedMaxLicenseUsers, setCustomUnlimitedMaxLicenseUsers] =
    useState(get(initialLicensePolicy, 'unlimited_max_license_users'));
  const [customUserMaxActivations, setCustomUserMaxActivations] = useState(
    get(initialLicensePolicy, 'max_activations'),
  );
  const [customAllowOfflineActivation, setCustomAllowOfflineActivation] =
    useState(get(initialLicensePolicy, 'allow_offline_activation'));
  const [customIsFloating, setCustomIsFloating] = useState(
    get(initialLicensePolicy, 'is_floating'),
  );
  const [customIsFloatingCloud, setCustomIsFloatingCloud] = useState(
    get(initialLicensePolicy, 'is_floating_cloud'),
  );
  const [customFloatingUsers, setCustomFloatingUsers] = useState(
    get(initialLicensePolicy, 'floating_users'),
  );
  const [customFloatingTimeout, setCustomFloatingTimeout] = useState(
    get(initialLicensePolicy, 'floating_timeout'),
  );
  const [customEnableMaintenancePeriod, setCustomEnableMaintenancePeriod] =
    useState(get(initialLicensePolicy, 'enable_maintenance_period'));
  const [customMaintenancePeriodValue, setCustomMaintenancePeriodValue] =
    useState(get(initialLicensePolicy, 'maintenance_duration'));
  const [customMaintenancePeriodLabel, setCustomMaintenancePeriodLabel] =
    useState('y');
  const [
    customSelectedMaintenanceDurationType,
    setCustomSelectedMaintenanceDurationType,
  ] = useState('duration');
  const [customMaintenanceEndDate, setCustomMaintenanceEndDate] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customLicenseNote, setCustomLicenseNote] = useState('');
  const [customCanBorrow, setCustomCanBorrow] = useState(
    get(initialLicensePolicy, 'can_borrow'),
  );
  const [customMaxBorrowTime, setCustomMaxBorrowTime] = useState(
    get(initialLicensePolicy, 'max_borrow_time'),
  );
  const [customRequireHwKey, setCustomRequireHwKey] = useState(
    get(initialLicensePolicy, 'require_hardware_key'),
  );
  const [customMetadata, setCustomMetadata] = useState('');

  const productsForSelector = mapProductsToSelector(products);
  const productAuthMethod = get(
    selectedProduct,
    'data.authorization_method',
    '',
  );
  const isUserBasedProduct = productAuthMethod === 'user';
  const selectedProductActive = get(selectedProduct, 'data.active');
  const selectedProductArchived = get(selectedProduct, 'data.is_archived');
  const isSelectedProductUnavailable =
    selectedProductActive === false || selectedProductArchived === true;
  const selectedProductUnavailableMessage = [
    selectedProductArchived ? t('This product is archived.') : null,
    selectedProductActive === false ? t('This product is disabled.') : null,
  ]
    .filter(Boolean)
    .join(' ');

  const getMaxTransfers = () => {
    if (preventMaxTransfers) {
      return -1;
    }
    if (hasMaxTransfers) {
      return Number(maxTransfers);
    }
    return 0;
  };

  const handleFeaturesChange = (updatedFeatures) => {
    markFeaturesDirty();
    setSelectedPolicyFeatures(preparePolicyFeatures(updatedFeatures));
  };

  const handleCustomFieldsSelect = (val) => {
    markCustomFieldsDirty();
    setSelectedPolicyCustomFields(val);
    return true;
  };

  const handleCustomFieldsEdit = (val) => {
    markCustomFieldsDirty();
    setSelectedPolicyCustomFields(val);
  };

  const getOrderData = () => {
    const licensePolicy = policies.find(
      (pp) => pp.code === selectedPolicy.code,
    );
    const product = products.find(
      (p) => p.id === get(selectedProduct, 'data.id'),
    );
    const hasCustomOverrides =
      useCustomValues &&
      (customPolicyDirty || featuresDirty || customFieldsDirty);

    let selectedPolicyForPayload = licensePolicy;

    const data = {
      id: get(product, 'id'),
      short_code: get(product, 'short_code'),
      allow_trial: get(product, 'allow_trial'),
      trial_days: get(product, 'trial_days'),
      product_name: get(product, 'product_name'),
      authorization_method: get(product, 'authorization_method'),
      license_num: isUserBasedProduct ? 1 : Number(licenseQuantity),
      license_templates: policies,
      selected_features: selectedPolicyFeatures,
      selected_custom_fields: selectedPolicyCustomFields,
      custom_values_used: useCustomValues,
      max_transfers: getMaxTransfers(),
    };

    // Add custom_overrides only if useCustomValues is true and at least one section is dirty
    if (hasCustomOverrides) {
      const custom_overrides = {};

      if (customPolicyDirty) {
        // Map license policy properties to API format
        custom_overrides.license_type = customLicenseType;
        // Keep default_license_type in sync so UI and payload reflect the override
        custom_overrides.default_license_type = customLicenseType;
        custom_overrides.max_activations = customMaxActivations;
        custom_overrides.allow_unlimited_activations =
          customAllowUnlimitedActivations;
        custom_overrides.valid_duration = `${customSubscriptionPeriodValue}${customSubscriptionPeriodLabel}`;
        custom_overrides.enable_maintenance_period =
          customEnableMaintenancePeriod;
        custom_overrides.maintenance_duration = `${customMaintenancePeriodValue}${customMaintenancePeriodLabel}`;
        custom_overrides.start_date = customStartDate;
        custom_overrides.validity_period = customSelectedValidityType;
        custom_overrides.is_trial = customIsTrial;
        custom_overrides.prevent_vm = customPreventVm;
        custom_overrides.max_transfers = getMaxTransfers();
        custom_overrides.can_borrow = customCanBorrow;
        custom_overrides.max_borrow_time = customMaxBorrowTime;
        custom_overrides.require_hardware_key = customRequireHwKey;
        custom_overrides.allow_grace_period = customAllowGracePeriod;
        custom_overrides.grace_period = customGracePeriod;
        custom_overrides.trial_days = customTrialDays;
        custom_overrides.max_consumptions = customMaxConsumptions;
        custom_overrides.allow_unlimited_consumptions =
          customAllowUnlimitedConsumptions;
        custom_overrides.allow_negative_consumptions =
          customAllowNegativeConsumptions;
        custom_overrides.allow_overages = customAllowOverages;
        custom_overrides.max_overages = customMaxOverages;
        custom_overrides.reset_consumption = customResetConsumption;
        custom_overrides.consumption_period = customResetConsumptionPeriod;
        custom_overrides.max_license_users = customMaxLicenseUsers;
        custom_overrides.unlimited_max_license_users =
          customUnlimitedMaxLicenseUsers;
        custom_overrides.allow_offline_activation =
          customAllowOfflineActivation;
        custom_overrides.is_floating = customIsFloating;
        custom_overrides.is_floating_cloud = customIsFloatingCloud;
        custom_overrides.floating_users = customFloatingUsers;
        custom_overrides.floating_timeout = customFloatingTimeout;
        custom_overrides.metadata = customMetadata;
        // Add other policy properties as needed
      }

      if (featuresDirty) {
        // Format features for API - don't send null or undefined attributes
        custom_overrides.product_features = selectedPolicyFeatures.map(
          (feature) =>
            Object.fromEntries(
              Object.entries(feature).filter(
                ([, value]) => value !== null && value !== undefined,
              ),
            ),
        );
      }

      if (customFieldsDirty) {
        // Custom fields are sent as an array of objects with name and value
        custom_overrides.custom_fields = selectedPolicyCustomFields.map(
          (field) => ({
            name: field.name,
            value: field.cf_value,
          }),
        );
      }

      data.custom_overrides = custom_overrides;
      selectedPolicyForPayload = {
        ...licensePolicy,
        ...custom_overrides,
        default_license_type: customLicenseType,
      };
    }

    data.selected_license_policy = selectedPolicyForPayload;

    return data;
  };

  const validateValue = async (val, cb, includeZero = false) => {
    setLoading(true);
    let errors;

    try {
      errors = await validateRequiredNumber(val, includeZero);
      setLoading(false);
      cb(errors);
    } catch (err) {
      setLoading(false);
    }

    if (errors) {
      return false;
    }
    return true;
  };

  const validateLicenseQuantity = async (val) => {
    setLoading(true);
    let errors;

    if (Number(val) > 99) {
      setLicenseQuantityError(`${errorMsg.maxQuantity} 99`);
      return false;
    }

    if (!isUserBasedProduct) {
      const remaining = getRemainingLicenses(
        get(selectedProduct, 'data.id'),
        counter,
      );
      // if null it is unlimited
      if (remaining !== null) {
        const isBeyondLimit = Number(val) > remaining;
        if (isBeyondLimit) {
          setLicenseQuantityError(`${errorMsg.remainingLicenses} ${remaining}`);
          return false;
        }
      }
    }

    try {
      errors = await validateRequiredNumber(val);
      setLoading(false);
      setLicenseQuantityError(errors);
    } catch (err) {
      setLoading(false);
    }

    if (errors) {
      return false;
    }
    return true;
  };

  const handleLicenseQuantityChange = (val) => {
    markCustomPolicyDirty();
    setLicenseQuantity(val);
    validateLicenseQuantity(val);
  };

  const handlePreventMaxTransfersChange = (val) => {
    markCustomPolicyDirty();
    setPreventMaxTransfers(val);
    setMaxTransfers(-1);
    if (val) {
      setHasMaxTransfers(false);
    }
  };

  const handleHasMaxTransfersChange = (val) => {
    markCustomPolicyDirty();
    setHasMaxTransfers(val);
    setMaxTransfers(getPolicyValue('max_transfers', selectedPolicy, 0));
    setMaxTransfersError('');
    if (val) {
      setPreventMaxTransfers(false);
    }
  };

  const handleMaxTransfersChange = (val) => {
    markCustomPolicyDirty();
    setMaxTransfers(val);
    validateValue(val, setMaxTransfersError);
  };

  // ------------- CUSTOM LICENSE POLICY HANDLERS -----------------------------------------------------------------------------------------------------------------------
  const handleCustomLicenseTypeChange = (val) => {
    markCustomPolicyDirty();
    setCustomLicenseType(val);
  };

  const handleCustomSubscriptionPeriodValueChange = (val) => {
    markCustomPolicyDirty();
    setCustomSubscriptionPeriodValue(val);
  };

  const handleCustomSubscriptionPeriodLabelChange = (val) => {
    markCustomPolicyDirty();
    setCustomSubscriptionPeriodLabel(val);
  };

  const handleCustomAllowGracePeriod = (val) => {
    markCustomPolicyDirty();
    setCustomAllowGracePeriod(val);
  };

  const handleCustomGracePeriodChange = (val) => {
    markCustomPolicyDirty();
    setCustomGracePeriod(val);
  };

  const handleCustomValidityTypeChange = (val) => {
    markCustomPolicyDirty();
    setCustomSelectedValidityType(val);
  };

  const handleCustomTimeLimitedPeriodValueChange = (val) => {
    markCustomPolicyDirty();
    setCustomTimeLimitedPeriodValue(val);
  };

  const handleCustomTimeLimitedPeriodLabelChange = (val) => {
    markCustomPolicyDirty();
    setCustomTimeLimitedPeriodLabel(val);
  };

  const handleCustomTimeLimitedEndDateChange = (val) => {
    markCustomPolicyDirty();
    setCustomTimeLimitedEndDate(val);
  };

  const handleCustomTrialChange = (val) => {
    markCustomPolicyDirty();
    setCustomIsTrial(val);
  };

  const handleCustomTrialTypeChange = (val) => {
    markCustomPolicyDirty();
    setCustomSelectedTrialType(val);
  };

  const handleCustomTrialDaysChange = (val) => {
    markCustomPolicyDirty();
    setCustomTrialDays(val);
  };

  const handleCustomTrialEndDateChange = (val) => {
    markCustomPolicyDirty();
    setCustomTrialEndDate(val);
  };

  const handleCustomMaxConsumptionsChange = (val) => {
    markCustomPolicyDirty();
    setCustomMaxConsumptions(val);
  };

  const handleCustomAllowUnlimitedConsumptions = (val) => {
    markCustomPolicyDirty();
    setCustomAllowUnlimitedConsumptions(val);
  };

  const handleCustomAllowNegativeConsumptions = (val) => {
    markCustomPolicyDirty();
    setCustomAllowNegativeConsumptions(val);
  };

  const handleCustomAllowOveragesChange = (val) => {
    markCustomPolicyDirty();
    setCustomAllowOverages(val);
  };

  const handleCustomSelectedOverageTypeChange = (val) => {
    markCustomPolicyDirty();
    setCustomSelectedOverageType(val);
  };

  const handleCustomMaxOveragesChange = (val) => {
    markCustomPolicyDirty();
    setCustomMaxOverages(val);
  };

  const handleCustomResetConsumptionChange = (val) => {
    markCustomPolicyDirty();
    setCustomResetConsumption(val);
  };

  const handleCustomResetConsumptionPeriodChange = (val) => {
    markCustomPolicyDirty();
    setCustomResetConsumptionPeriod(val);
  };

  const handleCustomMaxActivationsChange = (val) => {
    markCustomPolicyDirty();
    setCustomMaxActivations(val);
  };

  const handleCustomAllowUnlimitedActivations = (val) => {
    markCustomPolicyDirty();
    setCustomAllowUnlimitedActivations(val);
  };

  const handleCustomPreventVmChange = (val) => {
    markCustomPolicyDirty();
    setCustomPreventVm(val);
  };

  const handleCustomMaxLicenseUsers = (val) => {
    markCustomPolicyDirty();
    setCustomMaxLicenseUsers(val);
  };

  const handleCustomUnlimitedMaxLicenseUsers = (val) => {
    markCustomPolicyDirty();
    setCustomUnlimitedMaxLicenseUsers(val);
  };

  const handleCustomUserMaxActivationsChange = (val) => {
    markCustomPolicyDirty();
    setCustomUserMaxActivations(val);
  };

  const handleCustomAllowOfflineActivationChange = (val) => {
    markCustomPolicyDirty();
    setCustomAllowOfflineActivation(val);
  };

  const handleCustomIsFloatingChange = (val) => {
    markCustomPolicyDirty();
    setCustomIsFloating(val);
  };

  const handleCustomIsFloatingCloudChange = (val) => {
    markCustomPolicyDirty();
    setCustomIsFloatingCloud(val);
  };

  const handleCustomFloatingUsersChange = (val) => {
    markCustomPolicyDirty();
    setCustomFloatingUsers(val);
  };

  const handleCustomFloatingTimeoutChange = (val) => {
    markCustomPolicyDirty();
    setCustomFloatingTimeout(val);
  };

  const handleCustomEnableMaintenancePeriod = (val) => {
    markCustomPolicyDirty();
    setCustomEnableMaintenancePeriod(val);
  };

  const handleCustomMaintenancePeriodValueChange = (val) => {
    markCustomPolicyDirty();
    setCustomMaintenancePeriodValue(val);
  };

  const handleCustomMaintenancePeriodLabelChange = (val) => {
    markCustomPolicyDirty();
    setCustomMaintenancePeriodLabel(val);
  };

  const handleCustomSelectedMaintenanceDurationType = (val) => {
    markCustomPolicyDirty();
    setCustomSelectedMaintenanceDurationType(val);
  };

  const handleCustomMaintenanceEndDateChange = (val) => {
    markCustomPolicyDirty();
    setCustomMaintenanceEndDate(val);
  };

  const handleCustomStartDateChange = (val) => {
    markCustomPolicyDirty();
    setCustomStartDate(val);
  };

  const handleCustomLicenseNoteChange = (val) => {
    markCustomPolicyDirty();
    setCustomLicenseNote(val);
  };

  const handleCustomCanBorrow = (val) => {
    markCustomPolicyDirty();
    setCustomCanBorrow(val);
  };

  const handleCustomMaxBorrowTime = (val) => {
    markCustomPolicyDirty();
    setCustomMaxBorrowTime(val);
  };

  const handleCustomRequireHwKey = (val) => {
    markCustomPolicyDirty();
    setCustomRequireHwKey(val);
  };

  const handleCustomMetadata = (val) => {
    markCustomPolicyDirty();
    setCustomMetadata(val);
  };

  // ------------- SET LICENSE POLICY VALUES ----------------------------------------------------------------------------------------------------------------------------

  const setLicensePolicyValues = (selectedPol) => {
    setSelectedPolicyFeatures(
      preparePolicyFeatures(getSelectedPolicyFeatures(selectedPol)),
    );
    setSelectedPolicyCustomFields(
      get(selectedPol, 'license_custom_field_templates'),
    );

    // Reset custom policy state to new policy values
    setCustomLicenseType(get(selectedPol, 'default_license_type'));
    setCustomSubscriptionPeriodValue(1);
    setCustomSubscriptionPeriodLabel('y');
    setCustomAllowGracePeriod(false);
    setCustomGracePeriod(0);
    setCustomSelectedValidityType('valid_from');
    setCustomTimeLimitedPeriodValue(1);
    setCustomTimeLimitedPeriodLabel('d');
    setCustomTimeLimitedEndDate('');
    setCustomIsTrial(false);
    setCustomSelectedTrialType('trial_days');
    setCustomTrialDays(0);
    setCustomTrialEndDate('');
    setCustomMaxConsumptions(get(selectedPol, 'max_consumptions'));
    setCustomAllowUnlimitedConsumptions(
      get(selectedPol, 'allow_unlimited_consumptions'),
    );
    setCustomAllowNegativeConsumptions(
      get(selectedPol, 'allow_negative_consumptions'),
    );
    setCustomAllowOverages(get(selectedPol, 'allow_overages'));
    setCustomSelectedOverageType('overages-unlimited');
    setCustomMaxOverages(get(selectedPol, 'max_overages'));
    setCustomResetConsumption(get(selectedPol, 'reset_consumption'));
    setCustomResetConsumptionPeriod(get(selectedPol, 'consumption_period'));
    setCustomMaxActivations(get(selectedPol, 'max_activations'));
    setCustomAllowUnlimitedActivations(
      get(selectedPol, 'allow_unlimited_activations'),
    );
    setCustomPreventVm(get(selectedPol, 'prevent_vm'));
    setCustomMaxLicenseUsers(get(selectedPol, 'max_license_users'));
    setCustomUnlimitedMaxLicenseUsers(
      get(selectedPol, 'unlimited_max_license_users'),
    );
    setCustomUserMaxActivations(get(selectedPol, 'max_activations'));
    setCustomAllowOfflineActivation(
      get(selectedPol, 'allow_offline_activation'),
    );
    setCustomIsFloating(get(selectedPol, 'is_floating'));
    setCustomIsFloatingCloud(get(selectedPol, 'is_floating_cloud'));
    setCustomFloatingUsers(get(selectedPol, 'floating_users'));
    setCustomFloatingTimeout(get(selectedPol, 'floating_timeout'));
    setCustomEnableMaintenancePeriod(
      get(selectedPol, 'enable_maintenance_period'),
    );
    setCustomMaintenancePeriodValue(get(selectedPol, 'maintenance_duration'));
    setCustomMaintenancePeriodLabel('y');
    setCustomSelectedMaintenanceDurationType('duration');
    setCustomMaintenanceEndDate('');
    setCustomStartDate('');
    setCustomLicenseNote('');
    setCustomCanBorrow(get(selectedPol, 'can_borrow'));
    setCustomMaxBorrowTime(get(selectedPol, 'max_borrow_time'));
    setCustomRequireHwKey(get(selectedPol, 'require_hardware_key'));
    setCustomMetadata('');

    resetCustomizationDirtyFlags();
  };

  // ------------- PRODUCT INFORMATION METHODS --------------------------------------------------------------------------------------------------------------------------
  const handleProductSelect = (val) => {
    const selectedP = products.find((p) => get(p, 'product_name') === val);

    if (!selectedP) {
      return false;
    }

    const defaultPolicy = getDefaultLicensePolicy(
      products,
      userPolicies,
      selectedP,
    );
    setFormDirty(true);
    // reset state values
    const newSelectedProduct = {
      label: get(selectedP, 'product_name'),
      value: get(selectedP, 'product_name'),
      data: selectedP,
    };
    setSelectedProduct(newSelectedProduct);
    setSelectedPolicy(defaultPolicy);
    setPolicies(getPoliciesList(userPolicies, products, selectedP));
    setLicensePolicyValues(defaultPolicy);
    resetCustomizationDirtyFlags();
    setUseCustomValues(false);
    return true;
  };

  const handlePolicySelect = (val) => {
    const selectedPol = policies.find((pp) => pp.code === val);
    setSelectedPolicy(selectedPol);
    setLicensePolicyValues(selectedPol);
    resetCustomizationDirtyFlags();
  };

  // ------------- SUBMIT METHODS ---------------------------------------------------------------------------------------------------------------------------------------
  const handleStepSubmit = async () => {
    const isQuantityValid = await validateLicenseQuantity(licenseQuantity);
    if (loading || !isQuantityValid) {
      return false;
    }

    const data = getOrderData();
    const isEditing = !!productToEdit || false;
    return handleSubmit(data, isEditing);
  };

  const handleClose = () => {
    if (isFormDirty) {
      setDirtyFormDisplay(true);
    } else {
      closeCb();
    }
  };

  const shouldShowFeaturesTab =
    selectedPolicyFeatures.length > 0 ||
    selectedProduct?.data?.product_features?.length > 0;
  const shouldShowCustomFieldsTab =
    selectedProduct?.data?.custom_fields?.length > 0;
  const isTimeLimited =
    get(selectedPolicy, 'default_license_type') === licenseTypes.time_limited;
  const isSubscription =
    get(selectedPolicy, 'default_license_type') === licenseTypes.subscription;
  const isConsumption =
    get(selectedPolicy, 'default_license_type') === licenseTypes.consumption;
  const hasProductsAndPolicies =
    !isSectionForbidden &&
    !!productsForSelector.length &&
    !!userPolicies.length;
  const canBorrow = get(selectedPolicy, 'can_borrow');
  const canEditLicense = hasPermission('edit_license');
  const canEditLicenseProductFeatures = hasPermission(
    'edit_license_product_features',
  );
  const canEditLicenseCustomFields = hasPermission(
    'edit_license_custom_fields',
  );

  return (
    <SlidePane
      closeCb={handleClose}
      isOpen={isOpen}
      title={title}
      width={width}
    >
      <div className="SelectProductForm">
        <div className="confirm-btn">
          <Button
            theme="success"
            onClick={handleStepSubmit}
            disabled={
              loading || !hasProductsAndPolicies || isSelectedProductUnavailable
            }
          >
            {t('Confirm')}
          </Button>
        </div>
        <div className="SelectProductForm-main">
          {isSectionForbidden ? (
            <Notice theme="error" title={t('Missing permission')}>
              {t(
                'You do not have permission to access products or license templates.',
              )}
            </Notice>
          ) : !hasProductsAndPolicies ? (
            <div className="missing-products-alert">
              <IconInfo height="40px" width="40px" color="#ee5253" />
              <div className="text">
                {t('You have no enabled products and license policies.')}
              </div>
            </div>
          ) : (
            <div className="sections-wrapper">
              <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
                <TabsHeader>
                  <Tab key="licenseType">{t('Main')}</Tab>
                  {useCustomValues &&
                    canEditLicense &&
                    canEditLicenseProductFeatures &&
                    shouldShowFeaturesTab && (
                      <Tab key="productFeatures">{t('Product Features')}</Tab>
                    )}
                  {useCustomValues &&
                    canEditLicense &&
                    canEditLicenseCustomFields &&
                    shouldShowCustomFieldsTab && (
                      <Tab key="customFields">{t('Custom Fields')}</Tab>
                    )}
                </TabsHeader>
                <TabContent>
                  <div className="main-section">
                    <section>
                      <ProductInformation
                        products={productsForSelector}
                        productPolicies={policies}
                        handleProductSelect={handleProductSelect}
                        selectedProduct={selectedProduct}
                        selectedPolicy={get(selectedPolicy, 'code')}
                        handlePolicySelect={handlePolicySelect}
                        useCustomValues={useCustomValues}
                        setUseCustomValues={handleUseCustomValuesToggle}
                      />
                      {isSelectedProductUnavailable && (
                        <Notice
                          theme="error"
                          title={t(
                            'Selected product cannot be used for order creation',
                          )}
                        >
                          {selectedProductUnavailableMessage}
                        </Notice>
                      )}
                      {!isUserBasedProduct && (
                        <div className="section-row">
                          <Label
                            text={t('Number of license keys')}
                            inputId="license-num-input"
                          />
                          <NumberInput
                            handleChange={handleLicenseQuantityChange}
                            value={licenseQuantity}
                            error={licenseQuantityError}
                            min="1"
                            max="1000"
                            id="license-num-input"
                          />
                        </div>
                      )}
                      {!useCustomValues && (
                        <DescriptionTable
                          details={[
                            {
                              label: t('License Type'),
                              value: displayValue(
                                t(get(selectedPolicy, 'default_license_type')),
                              ),
                            },
                            {
                              label: t('Max activations'),
                              value: get(
                                selectedPolicy,
                                'allow_unlimited_activations',
                              )
                                ? t('Unlimited')
                                : displayValue(
                                    get(selectedPolicy, 'max_activations'),
                                  ),
                            },
                            {
                              label: isUserBasedProduct
                                ? t('Max license users')
                                : null,
                              value:
                                get(
                                  selectedPolicy,
                                  'unlimited_max_license_users',
                                ) ||
                                get(selectedPolicy, 'max_license_users') === 0
                                  ? t('Unlimited')
                                  : displayValue(
                                      get(selectedPolicy, 'max_license_users'),
                                    ),
                            },
                            {
                              label:
                                isTimeLimited || isSubscription
                                  ? t('Valid duration')
                                  : null,
                              value: displayValue(
                                get(selectedPolicy, 'valid_duration'),
                              ),
                            },
                            {
                              label: get(selectedPolicy, 'allow_grace_period')
                                ? t('Grace period')
                                : null,
                              value: `${displayValue(
                                get(selectedPolicy, 'grace_period'),
                              )} ${t('hours')}`,
                            },
                            {
                              label: t('Is trial'),
                              value: get(selectedPolicy, 'allow_trial')
                                ? t('Yes')
                                : t('No'),
                            },
                            {
                              label: get(selectedPolicy, 'allow_trial')
                                ? t('Trial days')
                                : null,
                              value: `${get(
                                selectedPolicy,
                                'trial_days',
                              )} days`,
                            },
                            // show consumption values
                            {
                              label: isConsumption
                                ? t('Max consumptions')
                                : null,
                              value: get(
                                selectedPolicy,
                                'allow_unlimited_consumptions',
                              )
                                ? t('Unlimited')
                                : displayValue(
                                    get(selectedPolicy, 'max_consumptions'),
                                  ),
                            },
                            {
                              label: isConsumption
                                ? t('Allow negative consumptions')
                                : null,
                              value: get(
                                selectedPolicy,
                                'allow_negative_consumptions',
                              )
                                ? t('Yes')
                                : t('No'),
                            },
                            {
                              label: isConsumption ? t('Allow overages') : null,
                              value: get(selectedPolicy, 'allow_overages')
                                ? t('Yes')
                                : t('No'),
                            },
                            {
                              label: isConsumption ? t('Max overages') : null,
                              value: displayMaxOveragesValue(selectedPolicy),
                            },
                            {
                              label: isConsumption
                                ? t('Reset consumption')
                                : null,
                              value: get(selectedPolicy, 'reset_consumption')
                                ? t('Yes')
                                : t('No'),
                            },
                            {
                              label: isConsumption
                                ? t('Consumption period')
                                : null,
                              value: displayValue(
                                capitalizeFirstLetter(
                                  get(selectedPolicy, 'consumption_period'),
                                ),
                              ),
                            },
                            // show everything else
                            {
                              label: get(
                                selectedPolicy,
                                'enable_maintenance_period',
                              )
                                ? t('Maintenance duration')
                                : null,
                              value: displayValue(
                                get(selectedPolicy, 'maintenance_duration'),
                              ),
                            },
                            {
                              label: get(selectedPolicy, 'is_floating')
                                ? t('Offline floating license')
                                : null,
                              value: get(selectedPolicy, 'is_floating')
                                ? t('Yes')
                                : t('No'),
                            },
                            {
                              label: get(selectedPolicy, 'is_floating_cloud')
                                ? t('Is floating cloud')
                                : null,
                              value: get(selectedPolicy, 'is_floating_cloud')
                                ? t('Yes')
                                : t('No'),
                            },
                            {
                              label:
                                get(selectedPolicy, 'is_floating') ||
                                get(selectedPolicy, 'is_floating_cloud')
                                  ? t('Max simultaneous license users')
                                  : null,
                              value: displayValue(
                                get(selectedPolicy, 'floating_users'),
                              ),
                            },
                            {
                              label:
                                get(selectedPolicy, 'is_floating') ||
                                get(selectedPolicy, 'is_floating_cloud')
                                  ? t('Floating timeout')
                                  : null,
                              value: `${displayValue(
                                get(selectedPolicy, 'floating_timeout'),
                              )} min`,
                            },
                            {
                              label: canBorrow ? t('Can borrow') : null,
                              value: canBorrow ? t('Yes') : t('No'),
                            },
                            {
                              label: canBorrow ? t('Max borrow time') : null,
                              value: `${displayValue(
                                get(selectedPolicy, 'max_borrow_time'),
                              )} ${t('hours')}`,
                            },
                            {
                              label: get(selectedPolicy, 'max_transfers')
                                ? t('Device transfer limit')
                                : null,
                              value: displayValue(
                                get(selectedPolicy, 'max_transfers'),
                              ),
                            },
                            {
                              label: t('Prevent virtual machine'),
                              value: get(selectedPolicy, 'prevent_vm')
                                ? t('Yes')
                                : t('No'),
                            },
                            {
                              label: shouldShowFeaturesTab
                                ? t('Product features')
                                : null,
                              value: (
                                <SelectedFeaturesTags
                                  features={selectedPolicyFeatures}
                                  product={selectedProduct}
                                />
                              ),
                            },
                            {
                              label: shouldShowCustomFieldsTab
                                ? t('Custom fields')
                                : null,
                              value: (
                                <SelectedCustomFieldsTags
                                  cFields={get(
                                    selectedPolicy,
                                    'license_custom_field_templates',
                                  )}
                                />
                              ),
                            },
                          ]}
                        />
                      )}
                    </section>

                    {useCustomValues && canEditLicense && (
                      <section>
                        <LicenseTypeSection
                          companyID={companyID}
                          handleLicenseTypeChange={
                            handleCustomLicenseTypeChange
                          }
                          licenseType={customLicenseType}
                          periodOptions={periodOptions}
                          handleSubscriptionPeriodValueChange={
                            handleCustomSubscriptionPeriodValueChange
                          }
                          subscriptionPeriodValue={
                            customSubscriptionPeriodValue
                          }
                          subscriptionPeriodLabel={
                            customSubscriptionPeriodLabel
                          }
                          handleSubscriptionPeriodLabelChange={
                            handleCustomSubscriptionPeriodLabelChange
                          }
                          allowGracePeriod={customAllowGracePeriod}
                          handleAllowGracePeriod={handleCustomAllowGracePeriod}
                          gracePeriod={customGracePeriod}
                          handleGracePeriodChange={
                            handleCustomGracePeriodChange
                          }
                          selectedValidityType={customSelectedValidityType}
                          handleValidityTypeChange={
                            handleCustomValidityTypeChange
                          }
                          handleTimeLimitedPeriodValueChange={
                            handleCustomTimeLimitedPeriodValueChange
                          }
                          timeLimitedPeriodValue={customTimeLimitedPeriodValue}
                          timeLimitedPeriodLabel={customTimeLimitedPeriodLabel}
                          handleTimeLimitedPeriodLabelChange={
                            handleCustomTimeLimitedPeriodLabelChange
                          }
                          handleTimeLimitedEndDateChange={
                            handleCustomTimeLimitedEndDateChange
                          }
                          timeLimitedEndDate={customTimeLimitedEndDate}
                          isTrial={customIsTrial}
                          handleTrialChange={handleCustomTrialChange}
                          selectedTrialType={customSelectedTrialType}
                          handleTrialTypeChange={handleCustomTrialTypeChange}
                          handleTrialDaysChange={handleCustomTrialDaysChange}
                          trialDays={customTrialDays}
                          handleTrialEndDateChange={
                            handleCustomTrialEndDateChange
                          }
                          trialEndDate={customTrialEndDate}
                          maxConsumptions={customMaxConsumptions}
                          handleMaxConsumptionsChange={
                            handleCustomMaxConsumptionsChange
                          }
                          allowUnlimitedConsumptions={
                            customAllowUnlimitedConsumptions
                          }
                          handleAllowUnlimitedConsumptions={
                            handleCustomAllowUnlimitedConsumptions
                          }
                          allowNegativeConsumptions={
                            customAllowNegativeConsumptions
                          }
                          handleAllowNegativeConsumptions={
                            handleCustomAllowNegativeConsumptions
                          }
                          allowOverages={customAllowOverages}
                          handleAllowOveragesChange={
                            handleCustomAllowOveragesChange
                          }
                          selectedOverageType={customSelectedOverageType}
                          handleSelectedOverageTypeChange={
                            handleCustomSelectedOverageTypeChange
                          }
                          maxOverages={customMaxOverages}
                          handleMaxOveragesChange={
                            handleCustomMaxOveragesChange
                          }
                          resetConsumption={customResetConsumption}
                          handleResetConsumptionChange={
                            handleCustomResetConsumptionChange
                          }
                          resetConsumptionPeriod={customResetConsumptionPeriod}
                          handleResetConsumptionPeriodChange={
                            handleCustomResetConsumptionPeriodChange
                          }
                          consumptionPeriodOptions={consumptionPeriodOptions}
                        />
                        <LicenseOptionsSection
                          companyID={companyID}
                          isUserBasedProduct={isUserBasedProduct}
                          licenseQuantity={licenseQuantity}
                          handleLicenseQuantityChange={
                            handleLicenseQuantityChange
                          }
                          licenseQuantityError={licenseQuantityError}
                          maxActivations={customMaxActivations}
                          handleMaxActivationsChange={
                            handleCustomMaxActivationsChange
                          }
                          allowUnlimitedActivations={
                            customAllowUnlimitedActivations
                          }
                          handleAllowUnlimitedActivations={
                            handleCustomAllowUnlimitedActivations
                          }
                          licenseUsers={{ emails: [] }}
                          handleLicenseUsersChange={() => {}}
                          preventVm={customPreventVm}
                          handlePreventVmChange={handleCustomPreventVmChange}
                          hasMaxTransfers={false}
                          handleHasMaxTransfersChange={
                            handleHasMaxTransfersChange
                          }
                          setPreventMaxTransfers={
                            handlePreventMaxTransfersChange
                          }
                          maxTransfers={0}
                          handleMaxTransfersChange={handleMaxTransfersChange}
                          isFloatingCloud={false}
                          maxLicenseUsers={customMaxLicenseUsers}
                          handleMaxLicenseUsers={handleCustomMaxLicenseUsers}
                          unlimitedMaxLicenseUsers={
                            customUnlimitedMaxLicenseUsers
                          }
                          handleUnlimitedMaxLicenseUsers={
                            handleCustomUnlimitedMaxLicenseUsers
                          }
                          userMaxActivations={customUserMaxActivations}
                          handleUserMaxActivationsChange={
                            handleCustomUserMaxActivationsChange
                          }
                          allowOfflineActivation={customAllowOfflineActivation}
                          handleAllowOfflineActivationChange={
                            handleCustomAllowOfflineActivationChange
                          }
                          maxTransfersError={maxTransfersError}
                        />
                        <LicenseUsageSection
                          companyID={companyID}
                          isFloating={customIsFloating}
                          handleIsFloatingChange={handleCustomIsFloatingChange}
                          isFloatingCloud={customIsFloatingCloud}
                          handleIsFloatingCloudChange={
                            handleCustomIsFloatingCloudChange
                          }
                          floatingUsers={customFloatingUsers}
                          handleFloatingUsersChange={
                            handleCustomFloatingUsersChange
                          }
                          floatingTimeout={customFloatingTimeout}
                          handleFloatingTimeoutChange={
                            handleCustomFloatingTimeoutChange
                          }
                          enableMaintenancePeriod={
                            customEnableMaintenancePeriod
                          }
                          handleProductHasMaintenanceChange={
                            handleCustomEnableMaintenancePeriod
                          }
                          periodOptions={periodOptions}
                          selectedMaintenanceDurationType={
                            customSelectedMaintenanceDurationType
                          }
                          maintenancePeriodValue={customMaintenancePeriodValue}
                          maintenancePeriodLabel={customMaintenancePeriodLabel}
                          handleMaintenancePeriodValueChange={
                            handleCustomMaintenancePeriodValueChange
                          }
                          handleMaintenancePeriodLabelChange={
                            handleCustomMaintenancePeriodLabelChange
                          }
                          maintenanceEndDate={customMaintenanceEndDate}
                          handleSelectedMaintenanceDurationType={
                            handleCustomSelectedMaintenanceDurationType
                          }
                          handleMaintenanceEndDateChange={
                            handleCustomMaintenanceEndDateChange
                          }
                          startDate={customStartDate}
                          handleStartDateChange={handleCustomStartDateChange}
                          licenseNote={customLicenseNote}
                          handleLicenseNoteChange={
                            handleCustomLicenseNoteChange
                          }
                          canBorrow={customCanBorrow}
                          setCanBorrow={handleCustomCanBorrow}
                          maxBorrowTime={customMaxBorrowTime}
                          setMaxBorrowTime={handleCustomMaxBorrowTime}
                          requireHwKey={customRequireHwKey}
                          setRequireHwKey={handleCustomRequireHwKey}
                          metadata={customMetadata}
                          setMetadata={handleCustomMetadata}
                        />
                      </section>
                    )}
                  </div>
                </TabContent>
                {useCustomValues &&
                  shouldShowFeaturesTab &&
                  canEditLicense &&
                  canEditLicenseProductFeatures && (
                    <TabContent>
                      <ProductFeaturesTab
                        productFeatures={get(
                          selectedProduct,
                          'data.product_features',
                          [],
                        )}
                        selectedProductFeatures={selectedPolicyFeatures}
                        onFeaturesChange={handleFeaturesChange}
                        showExpiryDate={true}
                      />
                    </TabContent>
                  )}
                {useCustomValues &&
                  shouldShowCustomFieldsTab &&
                  canEditLicense &&
                  canEditLicenseCustomFields && (
                    <TabContent>
                      <CustomFieldsTab
                        selectedProduct={selectedProduct}
                        selectedPolicy={selectedPolicy}
                        handleCustomFieldsSelect={handleCustomFieldsSelect}
                        selectedCustomFields={selectedPolicyCustomFields}
                        setCustomFieldsTabError={() => {}}
                        handleFieldsEdit={handleCustomFieldsEdit}
                      />
                    </TabContent>
                  )}
              </Tabs>
            </div>
          )}
        </div>
        {isDirtyFormDisplayed && (
          <DirtyFormAlert
            dirty={isFormDirty}
            closeAlert={() => setDirtyFormDisplay(false)}
            closeCb={closeCb}
          />
        )}
      </div>
    </SlidePane>
  );
};

SelectProductForm.propTypes = {
  closeCb: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  productToEdit: PropTypes.object,
  title: PropTypes.string.isRequired,
  width: PropTypes.string,
};

SelectProductForm.defaultProps = {
  productToEdit: null,
  width: '80%',
};

export default SelectProductForm;
